import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useCurrentUserQuery } from '@/features/auth/hooks/use-auth'
import { useAuthStore } from '@/features/auth/stores/auth-store'
import { ApiError } from '@/shared/api/http-client'
import { FullscreenLoader } from '@/shared/ui/fullscreen-loader'
import { Button } from '@/shared/ui/button'
import { resolveProtectedRouteState } from '@/app/routes/protected-route-state'
import i18n from '@/shared/i18n'

export function ProtectedRoute() {
  const location = useLocation()
  const token = useAuthStore((state) => state.token)
  const resetSession = useAuthStore((state) => state.reset)
  const { data: user, isPending, isError, isFetching, error, refetch } = useCurrentUserQuery({
    enabled: Boolean(token),
  })
  const isUnauthorized = isError && error instanceof ApiError && error.status === 401
  const routeState = resolveProtectedRouteState({
    hasToken: Boolean(token),
    hasUser: Boolean(user),
    isPending,
    isError,
    errorStatus: error instanceof ApiError ? error.status : undefined,
  })

  useEffect(() => {
    if (isUnauthorized) {
      resetSession()
    }
  }, [isUnauthorized, resetSession])

  const redirectTo = `${location.pathname}${location.search}${location.hash}`

  if (routeState === 'login') {
    return <Navigate to="/login" replace state={{ from: redirectTo }} />
  }

  if (routeState === 'loading' || routeState === 'resetting') {
    return <FullscreenLoader message={i18n.t('auth.checking_session')} />
  }

  if (routeState === 'error') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-[#09080b] px-5 text-white">
        <section className="w-full max-w-md rounded-2xl border border-white/10 bg-[#151018] p-6 text-center shadow-2xl">
          <p className="text-xs uppercase tracking-[0.28em] text-primary-soft/80">GLEAM</p>
          <h1 className="mt-3 text-2xl font-semibold">{i18n.t('auth.session_error_title')}</h1>
          <p className="mt-3 text-sm leading-6 text-slate-300">{i18n.t('auth.session_error_description')}</p>
          <div className="mt-6 grid gap-3">
            <Button onClick={() => void refetch()} disabled={isFetching}>
              {i18n.t('auth.session_retry')}
            </Button>
            <button
              type="button"
              className="rounded-xl px-4 py-3 text-sm text-slate-400 transition hover:bg-white/5 hover:text-white"
              onClick={resetSession}
            >
              {i18n.t('auth.session_login_again')}
            </button>
          </div>
        </section>
      </main>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: redirectTo }} />
  }

  return <Outlet />
}
